import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Компонент для ссылок на разделы
function SectionLink({ children, sectionId, className = '', onNavigate, onScrollToSection }) {
  const navigate = useNavigate();

  // Если это API - переход на другую страницу
  if (sectionId === 'api') {
    return (
      <a
        href="/api"
        onClick={(e) => {
          e.preventDefault();
          navigate('/api');
        }}
        className={`text-blue-600 hover:text-blue-800 underline font-semibold ${className}`}
      >
        {children}
      </a>
    );
  }

  // Для разделов внутри инструкции - прокрутка
  return (
    <a
      href={`#${sectionId}`}
      onClick={(e) => {
        e.preventDefault();
        const element = document.getElementById(sectionId);
        if (element) {
          // Обновляем активную секцию
          if (onScrollToSection) {
            onScrollToSection(sectionId);
          } else {
            window.dispatchEvent(new CustomEvent('setActiveSection', { detail: sectionId }));
          }
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }}
      className={`text-blue-600 hover:text-blue-800 underline font-semibold ${className}`}
    >
      {children}
    </a>
  );
}

function Instructions() {
  const [activeSection, setActiveSection] = useState('full-flow');

  // Слушаем события для обновления активной секции
  useEffect(() => {
    const handleSetActiveSection = (e) => {
      setActiveSection(e.detail);
    };
    window.addEventListener('setActiveSection', handleSetActiveSection);
    return () => window.removeEventListener('setActiveSection', handleSetActiveSection);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const allSections = ['full-flow', 'csv', 'registration', 'question-banks', 'test-schemas', 'invitations', 'results', 'payment', 'profile', 'password-reset', 'tips'];
      const scrollPosition = window.scrollY + 200;

      for (let i = allSections.length - 1; i >= 0; i--) {
        const element = document.getElementById(allSections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(allSections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'full-flow', label: 'Полный флоу работы' },
    { id: 'csv', label: 'CSV файлы' },
    { id: 'registration', label: 'Регистрация' },
    { id: 'question-banks', label: 'Базы вопросов' },
    { id: 'test-schemas', label: 'Тест-схемы' },
    { id: 'invitations', label: 'Приглашения' },
    { id: 'results', label: 'Результаты' },
    { id: 'payment', label: 'Оплата' },
    { id: 'profile', label: 'Личный кабинет' },
    { id: 'password-reset', label: 'Восстановление пароля' },
  ];

  const mainFlows = [
    { id: 'tips', label: 'Советы и рекомендации', description: 'Лучшие практики, частые ошибки и решение проблем' },
  ];

  return (
    <>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> Полная инструкция по использованию SkillGate
          </h1>
          <p className="text-lg text-gray-600 mb-4"> Подробное руководство по всем разделам и функциям платформы
          </p>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Примечание:</strong> Документация по API находится в отдельном разделе{' '}
              <a href="/api" className="text-blue-600 hover:text-blue-800 underline font-semibold">API</a> в меню.
            </p>
          </div>
        </div>

        {/* Навигация по разделам */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Быстрые переходы:</h3>
            <div className="flex flex-wrap gap-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeSection === section.id
                      ? section.id === 'full-flow'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-blue-600 text-white shadow-md'
                      : section.id === 'full-flow'
                        ? 'bg-blue-50 text-gray-700 hover:bg-blue-100 border border-blue-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
          {mainFlows.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Дополнительно:</h3>
              <div className="flex flex-wrap gap-2">
                {mainFlows.map((flow) => (
                  <button
                    key={flow.id}
                    onClick={() => {
                      setActiveSection(flow.id);
                      document.getElementById(flow.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                      activeSection === flow.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-blue-50 text-gray-700 hover:bg-blue-100 border border-blue-200'
                    }`}
                    title={flow.description}
                  >
                    {flow.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Полный флоу */}
        <section id="full-flow" className="mb-12">
          <div className="bg-blue-600 text-white rounded-xl shadow-lg p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white"> Полный флоу работы с платформой</h2>
            
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 1: Регистрация и вход</h3>
                <p className="text-white/90 mb-2">Зарегистрируйтесь, подтвердите email и войдите в систему.</p>
                <p className="text-white/80 text-sm">Вы получите 500 рублей бонусом на баланс!</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 2: Подготовка CSV файла</h3>
                <p className="text-white/90 mb-2">Создайте CSV файл с вопросами по шаблону (см. раздел <SectionLink sectionId="csv" onScrollToSection={setActiveSection}>"CSV файлы"</SectionLink>).</p>
                <p className="text-white/80 text-sm">Подготовьте изображения, если они нужны для вопросов.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 3: Создание базы вопросов</h3>
                <p className="text-white/90 mb-2">Загрузите CSV файл и изображения в разделе <SectionLink sectionId="question-banks" onScrollToSection={setActiveSection}>"Базы вопросов"</SectionLink>.</p>
                <p className="text-white/80 text-sm">Система автоматически распарсит файл и создаст базу.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 4: Создание тест-схемы</h3>
                <p className="text-white/90 mb-2">Выберите базу вопросов и укажите количество вопросов для кандидата.</p>
                <p className="text-white/80 text-sm">Система будет равномерно распределять вопросы по секциям.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 5: Создание приглашений</h3>
                <p className="text-white/90 mb-2">Создайте приглашение для каждого кандидата.</p>
                <p className="text-white/80 text-sm">Каждое приглашение получит уникальную ссылку и уникальный набор вопросов.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 6: Отправка кандидатам</h3>
                <p className="text-white/90 mb-2">Отправьте ссылку или QR-код кандидату через email, мессенджер или лично.</p>
                <p className="text-white/80 text-sm">Кандидат проходит тест в мобильном приложении SkillGate — это встроенная защита от списывания.</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/20">
                <h3 className="text-xl font-semibold mb-4 text-white">Шаг 7: Просмотр результатов</h3>
                <p className="text-white/90 mb-2">После завершения теста нажмите "Просмотреть результаты". Если на балансе достаточно средств (≥100 рублей), оплата произойдет автоматически. Если нет - пополните баланс (см. раздел "Оплата").</p>
                <p className="text-white/80 text-sm">Просмотрите ответы кандидата, добавьте обратную связь и измените статус.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: CSV файлы */}
        <section id="csv" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900"> Создание CSV файлов</h2>
        <a
          href="/template.zip"
          download="template.zip"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
        > Скачать шаблон
        </a>
      </div>

            <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-xl mb-6">
              <h3 className="text-yellow-800 mb-4 font-semibold text-xl"> ВАЖНО: Первая строка CSV</h3>
        <p className="mb-4 text-yellow-800 leading-relaxed font-semibold"> Первая строка CSV файла <strong>ВСЕГДА</strong> должна быть заголовком в точности такого формата:
        </p>
        <div className="bg-white p-4 rounded-lg border-2 border-yellow-400 mb-4 overflow-x-auto">
                <code className="text-sm text-gray-900 font-semibold block whitespace-pre-wrap break-words">
Название секции;Вопрос;Тип;Варианты ответов;Ответ;Вес;Пояснение веса;Время ответа (сек);Название картинки
          </code>
        </div>
        <p className="mb-0 text-yellow-800 leading-relaxed"> Эта строка обязательна и должна быть первой строкой файла. Без неё система не сможет правильно распарсить CSV.
        </p>
      </div>

            <div className="mb-6">
              <h3 className="text-blue-600 mb-4 font-semibold text-xl"> Структура CSV файла</h3>
        <p className="mb-4 leading-relaxed text-gray-700"> CSV-файл должен содержать следующие колонки, разделенные точкой с запятой (<code className="bg-gray-100 px-1.5 py-0.5 rounded">;</code>):
        </p>
        
              <div className="space-y-3">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">1. Название секции</p>
                  <p className="text-gray-700 text-sm">Категория вопроса (например, "Программирование", "Алгоритмы", "Базы данных"). Используется для группировки вопросов и равномерного распределения по секциям.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">2. Вопрос</p>
                  <p className="text-gray-700 text-sm">Текст вопроса, который увидит кандидат. Может содержать любые символы, включая переносы строк.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">3. Тип</p>
                  <p className="text-gray-700 text-sm mb-2">Тип вопроса:</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">text</code> - текстовый вопрос (кандидат вводит ответ)</li>
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">single</code> - одиночный выбор (radio button)</li>
                    <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">multiple</code> - множественный выбор (checkbox)</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">4. Варианты ответов</p>
                  <p className="text-gray-700 text-sm">Варианты для вопросов с выбором, разделенные символом <code className="bg-gray-100 px-1.5 py-0.5 rounded">|</code>. Для текстовых вопросов оставьте пустым.</p>
                  <p className="text-gray-700 text-sm mt-2">Пример: <code className="bg-gray-100 px-1.5 py-0.5 rounded">Вариант 1|Вариант 2|Вариант 3</code></p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">5. Ответ</p>
                  <p className="text-gray-700 text-sm mb-2">Правильный ответ:</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    <li>Для <code className="bg-gray-100 px-1.5 py-0.5 rounded">text</code> - текст правильного ответа</li>
                    <li>Для <code className="bg-gray-100 px-1.5 py-0.5 rounded">single</code> - индекс правильного варианта (начинается с 0)</li>
                    <li>Для <code className="bg-gray-100 px-1.5 py-0.5 rounded">multiple</code> - индексы через запятую без пробелов (например: <code className="bg-gray-100 px-1.5 py-0.5 rounded">0,2</code>)</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">6. Вес</p>
                  <p className="text-gray-700 text-sm">Числовое значение важности вопроса (например, 1, 2, 3). Используется для оценки результатов.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">7. Пояснение веса</p>
                  <p className="text-gray-700 text-sm">Описание важности вопроса (например, "Базовые знания", "Продвинутый уровень").</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">8. Время ответа (сек)</p>
                  <p className="text-gray-700 text-sm">Время на ответ в секундах (например, 30, 45, 60, 120). Таймер будет отсчитывать время для каждого вопроса.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">9. Название картинки</p>
                  <p className="text-gray-700 text-sm">Имя файла изображения (например, <code className="bg-gray-100 px-1.5 py-0.5 rounded">1.png</code>). Если изображение не требуется, оставьте пустым.</p>
                </div>
              </div>
      </div>

            <div className="mb-6">
              <h3 className="text-blue-600 mb-4 font-semibold text-xl"> Примеры типов вопросов</h3>
        
              <div className="space-y-6">
                <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Текстовый вопрос (text)</h4>
                  <pre className="bg-white p-4 rounded-md overflow-x-auto text-sm leading-relaxed whitespace-pre-wrap break-words">
{`Программирование;Что такое переменная?;text;;Именованная область памяти для хранения данных;1;Базовые знания;60;`}
            </pre>
                  <p className="mt-3 text-sm text-gray-700">Колонка "Варианты ответов" пустая, в "Ответ" указывается правильный текст.</p>
        </div>

                <div className="bg-green-50 p-5 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Одиночный выбор (single)</h4>
                  <pre className="bg-white p-4 rounded-md overflow-x-auto text-sm leading-relaxed whitespace-pre-wrap break-words">
{`Программирование;Какой из этих языков является компилируемым?;single;JavaScript|Python|C++|Ruby;2;1;Знание типов языков;45;`}
            </pre>
                  <p className="mt-3 text-sm text-gray-700"> Варианты: JavaScript (0), Python (1), C++ (2), Ruby (3). Правильный ответ: <code className="bg-gray-100 px-1.5 py-0.5 rounded">2</code> (C++).
                  </p>
        </div>

                <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Множественный выбор (multiple)</h4>
                  <pre className="bg-white p-4 rounded-md overflow-x-auto text-sm leading-relaxed whitespace-pre-wrap break-words">
{`Алгоритмы;Выберите все структуры данных, которые используют LIFO;multiple;Стек|Очередь|Дек|Массив;0,2;1;Знание структур данных;90;`}
            </pre>
                  <p className="mt-3 text-sm text-gray-700"> Правильные ответы: <code className="bg-gray-100 px-1.5 py-0.5 rounded">0,2</code> (Стек и Дек). Индексы через запятую без пробелов.
                  </p>
          </div>
        </div>
      </div>

            <div className="bg-blue-50 border-2 border-blue-500 p-6 rounded-xl">
              <h3 className="text-blue-700 mb-4 font-semibold text-xl"> Равномерное покрытие секций</h3>
        <p className="mb-4 leading-relaxed text-blue-900"> Система использует <strong>стратифицированную выборку</strong> для равномерного распределения вопросов по секциям.
        </p>
          <div className="bg-white p-4 rounded-lg mt-3">
            <p className="mb-2 font-semibold text-gray-900">Пример:</p>
            <p className="mb-1 text-sm text-gray-700">База: 12 вопросов (Программирование: 5, Алгоритмы: 3, БД: 2, Физика: 2)</p>
            <p className="mb-1 text-sm text-gray-700">Нужно выбрать: 6 вопросов</p>
            <p className="mb-2 text-sm text-gray-900 font-semibold"><strong>Результат:</strong> Программирование: 2-3, Алгоритмы: 1-2, БД: 1, Физика: 1</p>
                <p className="text-sm text-gray-600 m-0"> Это гарантирует, что кандидат будет проверен по всем категориям знаний равномерно.
            </p>
          </div>
        </div>
      </div>
        </section>

        {/* Раздел: Регистрация */}
        <section id="registration" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Регистрация и авторизация</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Шаг 1: Регистрация</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите на страницу регистрации (кнопка "Регистрация" на главной странице или <code className="bg-gray-100 px-1.5 py-0.5 rounded">/register</code>)</li>
                  <li>Введите ваш email адрес</li>
                  <li>Нажмите "Отправить код подтверждения"</li>
                  <li>Проверьте почту и введите полученный код (код действителен 15 минут)</li>
                  <li>Заполните форму регистрации:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Название компании</li>
                      <li>Пароль (минимум 6 символов)</li>
                      <li>Подтверждение пароля</li>
                    </ul>
                  </li>
                  <li>Примите условия использования:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Политика обработки данных</li>
                      <li>Пользовательское соглашение</li>
                      <li>Публичная оферта</li>
                    </ul>
                  </li>
                  <li>Нажмите "Зарегистрироваться"</li>
                </ol>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-4">
                  <p className="text-green-800 text-sm"><strong>После регистрации:</strong> Вы автоматически войдете в систему и получите бонусные 500 рублей на баланс!</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Шаг 2: Вход в систему</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите на страницу входа (<code className="bg-gray-100 px-1.5 py-0.5 rounded">/login</code>)</li>
                  <li>Введите email и пароль</li>
                  <li>Нажмите "Войти"</li>
                </ol>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-blue-800 text-sm"><strong>Совет:</strong> Токен авторизации сохраняется в браузере, поэтому вам не нужно входить каждый раз.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Базы вопросов */}
        <section id="question-banks" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Работа с базами вопросов</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Создание новой базы вопросов</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите в раздел <strong><SectionLink sectionId="question-banks" onScrollToSection={setActiveSection}>"Базы вопросов"</SectionLink></strong> в меню</li>
                  <li>Нажмите кнопку <strong>"Создать новую базу"</strong></li>
                  <li>Введите название базы (например, "Python разработчик", "Frontend разработчик")</li>
                  <li>Подготовьте CSV файл с вопросами (см. раздел <SectionLink sectionId="csv" onScrollToSection={setActiveSection}>"CSV файлы"</SectionLink>)</li>
                  <li>Загрузите CSV файл через кнопку "Выбрать CSV файл"</li>
                  <li>Если у вас есть изображения для вопросов:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Подготовьте все изображения, указанные в CSV</li>
                      <li>Выберите файлы изображений (можно выбрать несколько)</li>
                      <li>Имена файлов должны точно совпадать с именами в CSV</li>
                    </ul>
                  </li>
                  <li>Нажмите <strong>"Создать базу"</strong></li>
                </ol>
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-4">
                  <p className="text-yellow-800 text-sm"><strong>Важно:</strong> После создания базы вы не сможете изменить вопросы. Если нужно изменить вопросы, создайте новую базу.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Просмотр базы вопросов</h3>
                <p className="text-gray-700 mb-3">В разделе <SectionLink sectionId="question-banks" onScrollToSection={setActiveSection}>"Базы вопросов"</SectionLink> вы увидите список всех ваших баз:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Название базы</li>
                  <li>Количество вопросов в базе</li>
                  <li>Дата создания</li>
                  <li>Кнопка "Просмотреть" для детального просмотра всех вопросов</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Использование базы в тест-схемах</h3>
                <p className="text-gray-700">После создания базы вопросов вы можете использовать её для создания тест-схем. Одна база может использоваться в нескольких тест-схемах.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Тест-схемы */}
        <section id="test-schemas" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Создание и управление тест-схемами</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Создание новой тест-схемы</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите в раздел <strong><SectionLink sectionId="test-schemas" onScrollToSection={setActiveSection}>"Тест-схемы"</SectionLink></strong></li>
                  <li>Нажмите <strong>"Создать новую тест-схему"</strong></li>
                  <li>Выберите базу вопросов из списка (должна быть создана заранее)</li>
                  <li>Введите название тест-схемы (например, "Тест для Python разработчика")</li>
                  <li>Укажите количество вопросов для кандидата:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Система автоматически выберет указанное количество вопросов из базы</li>
                      <li>Вопросы будут распределены равномерно по секциям</li>
                      <li>Количество не должно превышать общее количество вопросов в базе</li>
                    </ul>
                  </li>
                  <li>Нажмите <strong>"Создать тест-схему"</strong></li>
                </ol>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-blue-800 text-sm"><strong>Совет:</strong> Рекомендуется создавать несколько тест-схем с разным количеством вопросов для разных уровней кандидатов.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Просмотр тест-схем</h3>
                <p className="text-gray-700 mb-3">В разделе <SectionLink sectionId="test-schemas" onScrollToSection={setActiveSection}>"Тест-схемы"</SectionLink> отображается:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Название тест-схемы</li>
                  <li>Количество вопросов</li>
                  <li>Количество созданных приглашений</li>
                  <li>Кнопки для управления приглашениями</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Как работает выбор вопросов</h3>
                <p className="text-gray-700 mb-3">При создании приглашения система:</p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                  <li>Берет все вопросы из выбранной базы</li>
                  <li>Группирует их по секциям</li>
                  <li>Выбирает указанное количество вопросов пропорционально из каждой секции</li>
                  <li>Создает уникальный набор вопросов для каждого кандидата</li>
                </ol>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-4">
                  <p className="text-green-800 text-sm"><strong>Преимущество:</strong> Каждый кандидат получает уникальный набор вопросов, что исключает возможность списывания.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Приглашения */}
        <section id="invitations" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Создание и отправка приглашений</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Создание приглашения</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите в раздел <strong><SectionLink sectionId="test-schemas" onScrollToSection={setActiveSection}>"Тест-схемы"</SectionLink></strong></li>
                  <li>Найдите нужную тест-схему и нажмите <strong>"Управление приглашениями"</strong></li>
                  <li>Введите название кандидата (например, "Иван Иванов" или "Кандидат #1")</li>
                  <li>Нажмите <strong>"Создать приглашение"</strong></li>
                  <li>Система создаст уникальную ссылку-приглашение</li>
                </ol>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-blue-800 text-sm"><strong>Важно:</strong> Каждое приглашение создает уникальный набор вопросов для кандидата, даже если используется одна и та же тест-схема.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Отправка приглашения кандидату</h3>
                <p className="text-gray-700 mb-3">После создания приглашения вы получите:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Уникальную ссылку</strong> для кандидата</li>
                  <li><strong>QR-код</strong> для быстрого доступа</li>
                  <li>Возможность скопировать ссылку</li>
                </ul>
                <p className="text-gray-700 mt-4 mb-3">Способы отправки:</p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Email:</strong> Скопируйте ссылку и отправьте кандидату по email</li>
                  <li><strong>Мессенджеры:</strong> Отправьте ссылку через Telegram, WhatsApp и т.д.</li>
                  <li><strong>QR-код:</strong> Кандидат может отсканировать QR-код камерой телефона</li>
                  <li><strong>Прямая ссылка:</strong> По ссылке кандидат попадает на страницу с инструкцией по установке мобильного приложения</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Просмотр всех приглашений</h3>
                <p className="text-gray-700 mb-3">В разделе <strong><SectionLink sectionId="invitations" onScrollToSection={setActiveSection}>"Приглашения"</SectionLink></strong> вы можете:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Просмотреть все созданные приглашения</li>
                  <li>Увидеть статус каждого приглашения (ожидает, завершено)</li>
                  <li>Открыть результаты теста (если тест завершен)</li>
                  <li>Скопировать ссылку приглашения</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Что видит кандидат</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Кандидат открывает ссылку, устанавливает мобильное приложение SkillGate и входит по коду приглашения</li>
                  <li>Видит экран приветствия с информацией о тесте</li>
                  <li>Нажимает "Приступить к тесту"</li>
                  <li>Система загружает изображения (если есть)</li>
                  <li>Кандидат проходит тест:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Видит вопросы по одному</li>
                      <li>Для каждого вопроса есть таймер</li>
                      <li>Может отвечать на вопросы разных типов</li>
                    </ul>
                  </li>
                  <li>После завершения видит экран "Тест завершен"</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Результаты */}
        <section id="results" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Просмотр результатов тестов</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Доступ к результатам</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите в раздел <strong><SectionLink sectionId="invitations" onScrollToSection={setActiveSection}>"Приглашения"</SectionLink></strong></li>
                  <li>Найдите завершенное приглашение (статус "Завершено")</li>
                  <li>Нажмите <strong>"Просмотреть результаты"</strong></li>
                  <li>Если результаты еще не оплачены, система проверит ваш баланс</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Оплата просмотра результатов</h3>
                <p className="text-gray-700 mb-3">Просмотр результатов стоит 100 рублей. Система автоматически проверит ваш баланс:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                  <li><strong>Если на балансе достаточно средств (≥100 рублей):</strong> Вам будет предложено оплатить просмотр результатов из баланса. Оплата происходит мгновенно, без ожидания одобрения администратора.</li>
                  <li><strong>Если средств недостаточно:</strong> Вам будет предложено пополнить баланс. Подробная инструкция по пополнению баланса находится в разделе <strong><SectionLink sectionId="payment" onScrollToSection={setActiveSection}>"Оплата"</SectionLink></strong>.</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-blue-800 text-sm"><strong>Совет:</strong> После пополнения баланса вы сможете сразу оплатить просмотр результатов, не дожидаясь одобрения администратором.</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-4">
                  <p className="text-yellow-800 text-sm"><strong>Важно:</strong> Пополнение баланса обрабатывается администратором вручную и может занять некоторое время.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Информация в результатах</h3>
                <p className="text-gray-700 mb-3">После оплаты вы увидите:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Все вопросы, которые были в тесте</li>
                  <li>Правильные ответы на каждый вопрос</li>
                  <li>Ответы кандидата</li>
                  <li>Вес каждого вопроса</li>
                  <li>Статистику поведения:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Количество сворачиваний приложения</li>
                      <li>Количество сделанных скриншотов</li>
                    </ul>
                  </li>
                  <li>Возможность добавить ручную проверку и обратную связь</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Ручная проверка и обратная связь</h3>
                <p className="text-gray-700 mb-3">Вы можете:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Изменить статус приглашения (прошел, не прошел, отложен)</li>
                  <li>Добавить ручную проверку в формате JSON</li>
                  <li>Написать обратную связь для кандидата</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Оплата */}
        <section id="payment" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Пополнение баланса и оплата</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Пополнение баланса</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Перейдите в раздел <strong><SectionLink sectionId="payment" onScrollToSection={setActiveSection}>"Оплата"</SectionLink></strong></li>
                  <li>Укажите сумму пополнения (в рублях)</li>
                  <li>Загрузите фото чека об оплате (скриншот или фото)</li>
                  <li>Нажмите <strong>"Отправить заявку"</strong></li>
                  <li>Дождитесь одобрения администратором</li>
                  <li>После одобрения баланс будет пополнен</li>
                </ol>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-4">
                  <p className="text-green-800 text-sm"><strong>Бонус:</strong> При регистрации вы получаете 500 рублей на баланс бесплатно!</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Статусы платежей</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>pending</strong> - ожидает обработки администратором</li>
                  <li><strong>approved</strong> - одобрен, баланс пополнен</li>
                  <li><strong>approved_custom</strong> - одобрен с другой суммой</li>
                  <li><strong>rejected</strong> - отклонен (с комментарием администратора)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Оплата просмотра результатов</h3>
                <p className="text-gray-700 mb-3">Просмотр результатов одного теста стоит 100 рублей. Стоимость списывается с баланса автоматически; если средств не хватает, пополните баланс (см. раздел "Оплата").</p>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Личный кабинет */}
        <section id="profile" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Личный кабинет</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Доступ к личному кабинету</h3>
                <p className="text-gray-700 mb-3">Чтобы открыть личный кабинет:</p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                  <li>Кликните на название вашей компании в сайдбаре (верхняя часть меню)</li>
                  <li>Или перейдите по адресу <code className="bg-gray-100 px-1.5 py-0.5 rounded">/profile</code></li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Информация в личном кабинете</h3>
                <p className="text-gray-700 mb-3">В личном кабинете отображается:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Название компании</strong> - название, указанное при регистрации</li>
                  <li><strong>Email</strong> - email адрес вашего аккаунта</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-blue-800 text-sm"><strong>В будущем:</strong> В личном кабинете будет доступна возможность редактирования профиля и другие настройки.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Раздел: Восстановление пароля */}
        <section id="password-reset" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Восстановление пароля</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Запрос восстановления пароля</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>На странице входа нажмите <strong>"Забыли пароль?"</strong></li>
                  <li>Введите email адрес вашего аккаунта</li>
                  <li>Нажмите "Отправить код восстановления"</li>
                  <li>Проверьте почту - вам придет код восстановления (действителен 15 минут)</li>
                </ol>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
                  <p className="text-blue-800 text-sm"><strong>Безопасность:</strong> Для защиты от перебора email, система всегда возвращает успешный ответ, даже если email не найден в системе.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Установка нового пароля</h3>
                <ol className="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                  <li>Введите email адрес</li>
                  <li>Введите код восстановления из письма</li>
                  <li>Введите новый пароль (минимум 6 символов)</li>
                  <li>Подтвердите новый пароль</li>
                  <li>Нажмите "Установить новый пароль"</li>
                </ol>
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-4">
                  <p className="text-yellow-800 text-sm"><strong>Важно:</strong> Код восстановления действителен только 15 минут. После использования кода он становится недействительным.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Советы и рекомендации */}
        <section id="tips" className="mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"> Советы и рекомендации</h2>
            
            <div className="space-y-4">
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <p className="font-semibold text-green-900 mb-2"> Лучшие практики:</p>
                <ul className="list-disc list-inside space-y-1 text-green-800 text-sm ml-4">
                  <li>Создавайте несколько тест-схем с разным количеством вопросов для разных уровней</li>
                  <li>Используйте понятные названия секций для лучшей организации</li>
                  <li>Тестируйте приглашения на себе перед отправкой кандидатам</li>
                  <li>Сохраняйте ссылки приглашений для быстрого доступа</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="font-semibold text-yellow-900 mb-2"> Частые ошибки:</p>
                <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm ml-4">
                  <li>Неправильный формат CSV (неправильный разделитель, отсутствие заголовка)</li>
                  <li>Несовпадение имен изображений в CSV и загруженных файлов</li>
                  <li>Указание количества вопросов больше, чем в базе</li>
                  <li>Забывание оплатить просмотр результатов перед попыткой их открыть</li>
                </ul>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="font-semibold text-blue-900 mb-2"> Решение проблем:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm ml-4">
                  <li>Если CSV не загружается - проверьте формат и кодировку (должна быть UTF-8)</li>
                  <li>Если изображения не отображаются - проверьте точное совпадение имен файлов</li>
                  <li>Если не можете войти - используйте восстановление пароля</li>
                  <li>Если результаты не открываются - убедитесь, что на балансе достаточно средств (от 100 рублей)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Instructions;
